import {
  LockOpenOutlined as LockOpenIcon,
  LockOutlined as LockIcon,
  HelpOutline,
  Info,
  InfoOutlined,
} from '@mui/icons-material';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormEventHandler, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { meQueryApiService } from '../../../../api/rtk-queries/extensions/meQueryApiService';
import {
  activateConsultantLogin,
  deactivateConsultantAccess,
  hideModal,
  showModal,
} from '../../../../features/consultantSettingsSlice';
import { RootState } from '../../../store';
import { useIsConsultant } from '../../../utils/utilHooks';
import {
  ButtonTypography,
  GreyOutlinedButton,
  PrimaryLoadingButton,
} from '../Buttons/buttons';
import {
  EditDialog,
  EditDialogTitle,
  EditorModus,
} from '../EditDialog/EditDialog';
import { useConfig } from '../../../utils/config';
import { checkTrailingSlash } from '../../../../api/client';

import { tr } from 'date-fns/locale';
import { EditPanelDataGrid } from '../EditPanelDataGrid/EditPanelDataGrid';
import VersjonInformasjonDialog from '../../../../features/VersjonsInformasjon/VersjonInformasjonDialog';

const useVersionStyles = makeStyles((theme) => ({
  lockSpinner: {
    position: 'absolute',
  },
  row: {
    borderTop: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.disabled,
    background: theme.palette.background.paper,
    padding: theme.spacing(0.5, 2),
    display: 'flex',
    justifyContent: 'center',
  },
  versionTag: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  iconButton: {
    width: 32,
    height: 32,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },
  },
}));

export function VersionToolbar(
  props: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >
): JSX.Element {
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);

  const handleOpenVersionDialog = () => {
    setVersionDialogOpen(true); // Åpner dialogen
  };

  const handleCloseVersionDialog = () => {
    setVersionDialogOpen(false); // Lukker dialogen
  };

  const config = useConfig();
  const hjelpUrl = config.urls.urlOnlineHjelp;
  const disabledLink = !hjelpUrl || hjelpUrl.length === 0;

  async function handleRedirect() {
    // const url = checkTrailingSlash(hjelpUrl);
    window.open(hjelpUrl);
  }
  const classes = useVersionStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isConsultant = useIsConsultant();
  const isLoading = useSelector(
    (state: RootState) => Number(state?.consultantSettings?.pending) > 0
  );
  const open = useSelector(
    (state: RootState) => state.consultantSettings.modalOpen
  );
  const [konsulentPassord, setKonsulentPassord] = useState('');
  const [isInputError, setIsInputError] = useState(false);
  const [finishedSnackbarOpen, setFinishedSnackbarOpen] = useState(false);
  const [activatedSnackbarOpen, setActivatedSnackbarOpen] = useState(false);
  const [hasBeenConsultant, setHasBeenConsultant] = useState(isConsultant);
  const [isNotifyProcess, setIsNotifyProcess] = useState(false);

  const [varsleWebsakPluss] = meQueryApiService.useVarsleWebsakPlussMutation();

  useEffect(() => {
    if (hasBeenConsultant && !isConsultant) {
      setFinishedSnackbarOpen(true);
    }
  }, [hasBeenConsultant, isConsultant]);

  useEffect(() => {
    if (isConsultant) {
      setActivatedSnackbarOpen(true);
      setHasBeenConsultant(true);
    }
  }, [isConsultant]);

  useEffect(() => setIsInputError(false), [open]);

  const toggleConsultant = () => {
    if (isLoading) return;

    if (isConsultant) {
      dispatch(deactivateConsultantAccess());
    } else {
      setKonsulentPassord('');
      dispatch(showModal());
    }
  };

  async function notifyWebsakPluss() {
    setIsNotifyProcess(true);
    await varsleWebsakPluss();
    const handler = setTimeout(() => {
      setIsNotifyProcess(false);
      clearTimeout(handler);
    }, 1000);
  }

  const activateConsultant: FormEventHandler<HTMLFormElement> = (ev) => {
    ev.preventDefault();
    dispatch(activateConsultantLogin(konsulentPassord));
  };

  const className = `${classes.row} ${props.className ?? ''}`.trim();
  
  return (
    <>
      <div {...props} className={className}>
        <Tooltip title={t('Versjonsoversikt')}>
          <IconButton
            aria-label={t('Versjonsoversikt')}
            onClick={handleOpenVersionDialog}
            color="inherit"
            className={classes.iconButton}
          >
            <InfoOutlined />
          </IconButton>
        </Tooltip>
        {/* Bruker dialogen */}
        <VersjonInformasjonDialog
          open={versionDialogOpen}
          onClose={handleCloseVersionDialog}
        />


        <Tooltip title={t('Åpne hjelp')}>
          <IconButton
            aria-label={t('Åpne hjelp')}
            onClick={handleRedirect}
            disabled={disabledLink}
            color="inherit"
            className={classes.iconButton}
          >
            <HelpOutline />
          </IconButton>
        </Tooltip>
        <Tooltip title={'Konsulentpålogging'}>
          <IconButton
            aria-label={t('Konsulentpålogging')}
            onClick={toggleConsultant}
            disabled={isLoading}
            color="inherit"
            className={classes.iconButton}
          >
            {isConsultant ? (
              <LockOpenIcon color="inherit" fontSize="inherit" />
            ) : (
              <LockIcon color="inherit" fontSize="inherit" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title={'Varsle WebSak. Bruk F5 for oppfrisking i WebSak'}>
          <span>
            <IconButton
              aria-label={t('VarsleWebsakPluss')}
              onClick={notifyWebsakPluss}
              disabled={isLoading}
              className={classes.iconButton}
              color="inherit"
              component={'span'}
            >
              <SyncIcon
                color="primary"
                fontSize="inherit"
                sx={
                  isNotifyProcess
                    ? {
                        animation: 'spin 2s linear infinite',
                        '@keyframes spin': {
                          '0%': {
                            transform: 'rotate(360deg)',
                          },
                          '100%': {
                            transform: 'rotate(0deg)',
                          },
                        },
                      }
                    : undefined
                }
              />
            </IconButton>
          </span>
        </Tooltip>
      </div>
      <Dialog
        open={open}
        onClose={() => dispatch(hideModal())}
        fullWidth
        maxWidth="xs"
      >
        <EditDialogTitle id="konsulentpålogging-dialog-title">
          {t('Aktiver konsulentpålogging')}
        </EditDialogTitle>
        <form onSubmit={activateConsultant}>
          <DialogContent sx={{ pt: 0 }}>
            <Tooltip
              title={
                (konsulentPassord?.length ?? 0) === 0
                  ? t('Vennligst fyll ut dette feltet.').toString()
                  : ''
              }
            >
              <span>
                <TextField
                  autoFocus
                  title={''}
                  error={isInputError}
                  type="text"
                  inputProps={{
                    pattern: '\\s*(?:[0-9]\\s*){6}\\s*',
                    autoComplete: 'one-time-code',
                  }}
                  inputMode="numeric"
                  label={t('Passord')}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={konsulentPassord}
                  required
                  onFocus={(e) => {
                    e.stopPropagation();
                  }}
                  onBlur={() => setIsInputError(konsulentPassord.length === 0)}
                  onChange={(ev) => setKonsulentPassord(ev.target.value)}
                  helperText={t('6-sifret kode fra autentiseringsapp')}
                />
              </span>
            </Tooltip>
          </DialogContent>
          <DialogActions>
            <GreyOutlinedButton
              test-id={'konsulentpålogging-avbryt-btn'}
              onClick={() => dispatch(hideModal())}
            >
              <ButtonTypography>{t('Avbryt')}</ButtonTypography>
            </GreyOutlinedButton>

            <PrimaryLoadingButton
              type="submit"
              disabled={konsulentPassord.length === 0}
              loading={isLoading}
              test-id={'konsulentpålogging-lagre-btn'}
            >
              <ButtonTypography>
                {t('Aktiver konsulentpålogging')}
              </ButtonTypography>
            </PrimaryLoadingButton>
          </DialogActions>
        </form>
      </Dialog>
      <Snackbar
        open={finishedSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setFinishedSnackbarOpen(false)}
      >
        <Alert severity="info">{t('Konsulentpålogging avsluttet')}</Alert>
      </Snackbar>
      <Snackbar
        open={activatedSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setActivatedSnackbarOpen(false)}
      >
        <Alert severity="warning">{t('Konsulentpålogging aktivert')}</Alert>
      </Snackbar>
    </>
  );
}
